import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'

import dashboardData from './generated/dashboardData.json'
import { Icon } from './components/Icon'
import { KnowledgeCanvas } from './components/KnowledgeCanvas'
import { MaterialUploadPanel } from './components/MaterialUploadPanel'
import { useObsidianVault } from './hooks/useObsidianVault'
import { OBSIDIAN_HOME_URL } from './lib/obsidianVault'
import type {
  DashboardData,
  DataSource,
  Task,
  VaultConnectionStatus,
} from './types'

const data = dashboardData as DashboardData

function formatSyncTime(value: string) {
  const parsed = new Date(value)

  if (Number.isNaN(parsed.getTime())) {
    return '同步时间未知'
  }

  return new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(parsed)
}

function PanelHeader({
  count,
  icon,
  kicker,
  title,
}: {
  count?: string
  icon: ReactNode
  kicker?: string
  title: string
}) {
  return (
    <header className="panel-header">
      <div className="panel-title">
        <span className="panel-title__icon">{icon}</span>
        <div>
          {kicker ? <span className="panel-kicker">{kicker}</span> : null}
          <h2>{title}</h2>
        </div>
      </div>
      {count ? <span className="panel-count">{count}</span> : null}
    </header>
  )
}

function StatusChip({ status }: { status: string }) {
  const isReady = ['已完成', '已掌握', '已同步', '已记录'].includes(status)
  return (
    <span className={`status-chip${isReady ? ' status-chip--success' : ''}`}>
      <i aria-hidden="true" />
      {status}
    </span>
  )
}

function StudentPanel() {
  const { student } = data

  return (
    <section aria-labelledby="student-panel-heading" className="panel-frame student-panel">
      <PanelHeader icon={<Icon name="user" />} title="学生概况" />
      <div className="student-profile">
        <div className="student-avatar"><Icon name="user" size={35} /></div>
        <div>
          <h3 id="student-panel-heading">{student.id}</h3>
          <span>{student.stage} · {student.subject}</span>
        </div>
      </div>
      <dl className="student-data">
        <div><dt>地区</dt><dd>{student.region}</dd></div>
        <div><dt>当前阶段</dt><dd>{student.phase}</dd></div>
        <div><dt>中考成绩</dt><dd>{student.score}</dd></div>
        <div><dt>当前教材</dt><dd>{student.textbookVersion}</dd></div>
      </dl>
    </section>
  )
}

function TaskPanel({ tasks }: { tasks: Task[] }) {
  const completed = tasks.filter((task) => task.status === '已完成').length

  return (
    <section aria-labelledby="task-panel-heading" className="panel-frame task-panel">
      <PanelHeader count={`${completed} / ${tasks.length}`} icon={<Icon name="clipboard" />} title="今日任务" />
      <p className="panel-description">当前学习流程中的待办事项</p>
      <ol className="command-list" id="task-panel-heading">
        {tasks.map((task, index) => (
          <li key={task.id}>
            <span className="command-index">{String(index + 1).padStart(2, '0')}</span>
            <strong>{task.title}</strong>
            <StatusChip status={task.status} />
          </li>
        ))}
      </ol>
    </section>
  )
}

function LearningPulse({ localMaterials }: { localMaterials: number }) {
  const metrics = [
    ['任务进度', data.summary.tasksDone, data.summary.tasksTotal, '%'],
    ['知识模块', data.summary.masteredKnowledge, data.summary.knowledgeTotal, '%'],
    ['测评记录', data.summary.assessmentsCount, undefined, '次'],
    ['动态资料', data.summary.materialsCount + localMaterials, undefined, '条'],
  ] as const

  return (
    <section aria-labelledby="pulse-heading" className="panel-frame pulse-panel">
      <PanelHeader icon={<Icon name="shield" />} title="学习脉搏" />
      <div className="pulse-grid" id="pulse-heading">
        {metrics.map(([label, value, denominator, unit]) => {
          const percent = denominator ? Math.round((value / denominator) * 100) : value
          return (
            <div key={label}>
              <span>{label}</span>
              <strong>{value}{denominator === undefined ? null : <small>/{denominator}</small>}</strong>
              <em>{denominator ? `${percent}%` : unit}</em>
            </div>
          )
        })}
      </div>
    </section>
  )
}

function PhasePanel() {
  return (
    <section aria-label="阶段目标" className="panel-frame phase-panel">
      <PanelHeader icon={<Icon name="flag" />} title="阶段目标" />
      <div className="phase-line">
        <span>入学准备阶段 · 高一数学</span>
        <strong>0%</strong>
      </div>
      <div aria-label="阶段完成度 0%" aria-valuemax={100} aria-valuemin={0} aria-valuenow={0} className="progress-track" role="progressbar">
        <span style={{ width: '0%' }} />
      </div>
    </section>
  )
}

function AssessmentPanel() {
  return (
    <section aria-labelledby="assessment-heading" className="panel-frame assessment-panel">
      <PanelHeader count={`${data.summary.assessmentsCount} 次`} icon={<Icon name="clipboard" />} title="最近测评" />
      <p className="panel-description">最多展示最近 5 次学校测评</p>
      <div id="assessment-heading">
        {data.assessments.length > 0 ? (
          <div className="assessment-list">
            {data.assessments.map((assessment) => (
              <article key={assessment.id}>
                <div><strong>{assessment.name}</strong><span>{assessment.subject} · {assessment.date}</span></div>
                <b>{assessment.result}</b>
              </article>
            ))}
          </div>
        ) : (
          <div className="assessment-empty">
            <span><Icon name="clipboard" size={23} /></span>
            <strong>尚未录入测评</strong>
            <p>学校小测、周测和大考登记到本地表格后会自动显示。</p>
          </div>
        )}
      </div>
    </section>
  )
}

function VaultPanel({
  message,
  onConnect,
  status,
  vaultName,
}: {
  message: string
  onConnect: () => Promise<boolean>
  status: VaultConnectionStatus
  vaultName: string
}) {
  const vault = data.knowledgeVault
  const metrics = [
    ['Markdown 文档', vault.noteCount, '个'],
    ['已审核', vault.approvedCount, '个'],
    ['待审核', vault.pendingCount, '个'],
    ['资料记录', data.summary.materialsCount, '条'],
  ]

  const connectionLabel = {
    checking: '检查中',
    unsupported: '浏览器不支持',
    disconnected: '连接知识库',
    'permission-needed': '重新授权',
    connecting: '连接中…',
    connected: `已连接 ${vaultName}`,
  }[status]

  return (
    <section aria-labelledby="vault-heading" className="panel-frame vault-panel" id="vault-panel">
      <PanelHeader icon={<Icon name="folder" />} title="知识库（本地 · Obsidian 兼容）" />
      <div className="mini-metrics" id="vault-heading">
        {metrics.map(([label, value, unit]) => (
          <div key={label}><span>{label}</span><strong>{value}<small>{unit}</small></strong></div>
        ))}
      </div>
      <div className="vault-actions">
        <a href={OBSIDIAN_HOME_URL}><Icon name="external" size={15} />打开 Obsidian 知识库</a>
        {status !== 'unsupported' ? (
          <button
            disabled={status === 'checking' || status === 'connecting'}
            onClick={() => void onConnect()}
            type="button"
          >
            <Icon name={status === 'connected' ? 'check' : 'link'} size={15} />
            {connectionLabel}
          </button>
        ) : null}
      </div>
      <p className="vault-message" title={message}>{message}</p>
      <p className="path-line"><Icon name="file" size={15} />{vault.root}</p>
    </section>
  )
}

function SourceItem({ source }: { source: DataSource }) {
  return (
    <div className="source-item">
      <span>{source.name}</span>
      <strong>{source.records}</strong>
      <StatusChip status={source.status} />
    </div>
  )
}

function SourcePanel() {
  return (
    <section aria-labelledby="source-heading" className="panel-frame bottom-panel source-panel">
      <PanelHeader icon={<Icon name="shield" />} title="数据来源健康度" />
      <div className="source-grid" id="source-heading">
        {data.sources
          .filter((source) => source.name !== 'Obsidian 知识库')
          .map((source) => <SourceItem key={source.file} source={source} />)}
      </div>
    </section>
  )
}

function PipelinePanel() {
  const steps = [
    ['本地采集', '完成', 'database'],
    ['结构校验', '通过', 'shield'],
    ['知识关联', '待补充', 'book'],
    ['驾驶舱同步', '完成', 'refresh'],
  ] as const

  return (
    <section aria-labelledby="pipeline-heading" className="panel-frame bottom-panel pipeline-panel">
      <PanelHeader icon={<Icon name="refresh" />} title="同步管道（本地优先）" />
      <div className="pipeline" id="pipeline-heading">
        {steps.map(([label, status, icon], index) => (
          <div className="pipeline__step" key={label}>
            <span><Icon name={icon} size={19} /></span>
            <strong>{label}</strong>
            <small className={status === '待补充' ? 'is-pending' : ''}>{status}</small>
            {index < steps.length - 1 ? <i aria-hidden="true" /> : null}
          </div>
        ))}
      </div>
    </section>
  )
}

function StoragePanel() {
  const [storageText, setStorageText] = useState('正在读取本机存储…')
  const [usagePercent, setUsagePercent] = useState(0)
  const [persistenceText, setPersistenceText] = useState('管理本地存储空间')

  useEffect(() => {
    let isActive = true

    if (!navigator.storage?.estimate) {
      setStorageText('浏览器未提供容量估算')
      return undefined
    }

    navigator.storage.estimate().then((estimate) => {
      if (!isActive) {
        return
      }

      const usedMb = (estimate.usage ?? 0) / 1024 / 1024
      const quotaGb = (estimate.quota ?? 0) / 1024 / 1024 / 1024
      const ratio = estimate.quota ? ((estimate.usage ?? 0) / estimate.quota) * 100 : 0
      setStorageText(`已用 ${usedMb.toFixed(1)} MB · 可用配额 ${quotaGb.toFixed(1)} GB`)
      setUsagePercent(Math.max(2, Math.min(100, ratio)))
    }).catch(() => {
      if (isActive) {
        setStorageText('无法读取本机存储容量')
      }
    })

    return () => {
      isActive = false
    }
  }, [])

  const requestPersistence = useCallback(async () => {
    if (!navigator.storage?.persist) {
      setPersistenceText('当前浏览器不支持固定存储')
      return
    }

    const persisted = await navigator.storage.persist()
    setPersistenceText(persisted ? '本地存储已设为持久保留' : '浏览器未授予持久保留')
  }, [])

  return (
    <section aria-labelledby="storage-heading" className="panel-frame storage-panel">
      <PanelHeader icon={<Icon name="database" />} title="本地存储状态" />
      <div className="storage-state" id="storage-heading">
        <div><Icon name="check" size={18} /><strong>仅保存在本地设备</strong></div>
        <div aria-label={`存储使用约 ${usagePercent.toFixed(1)}%`} className="storage-meter"><span style={{ width: `${usagePercent}%` }} /></div>
        <span>{storageText}</span>
        <button onClick={() => void requestPersistence()} type="button"><Icon name="settings" size={14} />{persistenceText}</button>
      </div>
    </section>
  )
}

export function App() {
  const [selectedKnowledgeId, setSelectedKnowledgeId] = useState('')
  const [localMaterialCount, setLocalMaterialCount] = useState(0)
  const obsidian = useObsidianVault()
  const handleMaterialCountChange = useCallback((count: number) => setLocalMaterialCount(count), [])
  const syncTime = useMemo(() => formatSyncTime(data.generatedAt), [])
  const handleToggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      void document.exitFullscreen()
      return
    }

    void document.documentElement.requestFullscreen()
  }, [])

  return (
    <>
      <a className="skip-link" href="#dashboard-main">跳到主要内容</a>

      <header className="command-header">
        <div className="command-header__inner">
          <div className="brand-lockup">
            <strong>stuDNA</strong>
            <span>学习驾驶舱</span>
          </div>
          <div className="header-context" aria-label="当前学习信息">
            <span><Icon name="book" />{data.student.subject}</span>
            <span><Icon name="user" />{data.student.id}</span>
            <span><Icon name="flag" />{data.student.phase}</span>
          </div>
          <div className="header-actions">
            <span className="sync-state"><Icon name="check" size={16} />本地数据已同步 <b>{syncTime}</b></span>
            <div className="header-utilities">
              <a aria-label="打开 Obsidian 知识库" href={OBSIDIAN_HOME_URL} title="打开 Obsidian 知识库"><Icon name="folder" size={17} /></a>
              <a aria-label="知识库连接设置" href="#vault-panel" title="知识库连接设置"><Icon name="settings" size={17} /></a>
              <button aria-label="切换全屏" onClick={handleToggleFullscreen} title="切换全屏" type="button"><Icon name="expand" size={17} /></button>
            </div>
          </div>
        </div>
      </header>

      <main className="command-shell" id="dashboard-main">
        <div className="command-grid">
          <aside className="left-rail">
            <StudentPanel />
            <TaskPanel tasks={data.tasks} />
            <LearningPulse localMaterials={localMaterialCount} />
            <PhasePanel />
            <VaultPanel
              message={obsidian.message}
              onConnect={obsidian.connect}
              status={obsidian.status}
              vaultName={obsidian.vaultName}
            />
          </aside>

          <div className="center-rail">
            <KnowledgeCanvas
              items={data.knowledge}
              onSelect={setSelectedKnowledgeId}
              selectedId={selectedKnowledgeId}
            />
            <div className="center-bottom-grid">
              <SourcePanel />
              <PipelinePanel />
            </div>
          </div>

          <aside className="right-rail">
            <MaterialUploadPanel
              knowledge={data.knowledge}
              materials={data.materials}
              obsidianMessage={obsidian.message}
              obsidianStatus={obsidian.status}
              onConnectObsidian={obsidian.connect}
              onCountChange={handleMaterialCountChange}
              onWriteToObsidian={obsidian.writeMaterial}
              studentId={data.student.id}
            />
            <AssessmentPanel />
            <StoragePanel />
          </aside>
        </div>
      </main>

      <footer className="command-footer">
        所有数据均保存在本地设备；连接 Obsidian 后，图片会写入本机知识库，未经允许不会同步到任何云端服务。
      </footer>
    </>
  )
}
